'use client'

// frontend/src/components/coworkers/CoworkerAvatar.tsx
//
// The face of a coworker. Reads lib/coworkers.ts, shows the photo from
// public/coworkers/<key>.jpg and falls back to a monogram on the stage colour
// if the file is missing, so a coworker never renders a broken image.
//
// Ported from h2r and Driver Auto. Changes: six PA coworkers, ring and
// monogram from the stage tokens, sizes 22, 26, 28, 32, 38, 44, 52 and 88.

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { resolveCoworker } from '@/lib/coworkers'

export type CoworkerAvatarSize = 22 | 26 | 28 | 32 | 38 | 44 | 52 | 56 | 88

const MONOGRAM_TEXT: Record<CoworkerAvatarSize, string> = {
  22: 'text-xs',
  26: 'text-xs',
  28: 'text-xs',
  32: 'text-sm',
  38: 'text-base',
  44: 'text-lg',
  52: 'text-xl',
  56: 'text-xl',
  88: 'text-3xl',
}

export interface CoworkerAvatarProps {
  /** A coworker key ("hannah") or first name ("Hannah"). */
  coworker: string
  size?: CoworkerAvatarSize
  /** A soft ring for the coworker that is working right now. */
  active?: boolean
  /**
   * True when the name is printed beside the face. The image then has an
   * empty alt so a screen reader does not say the name twice.
   */
  named?: boolean
  className?: string
}

export function CoworkerAvatar({
  coworker,
  size = 32,
  active = false,
  named = false,
  className,
}: CoworkerAvatarProps) {
  const person = resolveCoworker(coworker)
  const [photoFailed, setPhotoFailed] = useState(false)
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

  const name = person?.name ?? coworker
  const monogram = (name || '?').trim().charAt(0).toUpperCase()
  const box = { width: size, height: size }

  return (
    <span
      className={cn('relative inline-flex shrink-0', className)}
      style={box}
      title={named ? undefined : name}
    >
      {active && (
        <span
          aria-hidden='true'
          className='absolute inset-0 animate-pulse rounded-full ring-4 ring-brand-cornflower/40'
        />
      )}
      {person && !photoFailed ? (
        // A small square from /public; next/image buys nothing here.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={`${basePath}${person.photo}`}
          alt={named ? '' : name}
          width={size}
          height={size}
          onError={() => setPhotoFailed(true)}
          className={cn(
            'relative rounded-full object-cover ring-2',
            person.tone.ring
          )}
          style={box}
        />
      ) : (
        <span
          role={named ? undefined : 'img'}
          aria-label={named ? undefined : name}
          aria-hidden={named ? true : undefined}
          className={cn(
            'relative inline-flex items-center justify-center rounded-full',
            'font-display font-semibold text-white',
            person ? person.tone.solid : 'bg-brand-navy',
            MONOGRAM_TEXT[size]
          )}
          style={box}
        >
          {monogram}
        </span>
      )}
    </span>
  )
}
